/**
 * Suppliers Section for Project Part Detail Page
 * Displays supplier assignments with RFP progress flow
 */

import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  Plus,
  CheckCircle,
  Circle,
  Envelope,
  X,
  Spinner,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import DirectSearchComponent from "shared/components/searchBar/components/directSearchComponent";
import { ResourceType } from "shared/types/resource";
import {
  ProjectPart,
  DocumentType,
  DocumentApprovalStatus,
} from "shared/types/renovation";
import { ContactCompany } from "shared/types/contact";

interface PartSuppliersSectionProps {
  part: ProjectPart;
  projectId: string;
  onUpdate: () => void;
}

export const PartSuppliersSection: React.FC<PartSuppliersSectionProps> = ({
  part,
  projectId,
  onUpdate,
}) => {
  const { session } = useSession();
  const navigate = useNavigate();
  const [showSupplierSearch, setShowSupplierSearch] = useState(false);
  const [supplierLoading, setSupplierLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Focus search input when shown
  useEffect(() => {
    if (showSupplierSearch) {
      setTimeout(() => {
        const inputElement = searchRef.current?.querySelector("input");
        if (inputElement) {
          inputElement.focus();
        }
      }, 100);
    }
  }, [showSupplierSearch]);

  // Handle supplier selection
  const handleSupplierSelect = async (supplierId: number) => {
    if (!session || !part.id) return;

    try {
      setSupplierLoading(true);

      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/part/${part.id}/supplier/${supplierId}`,
        { method: "POST" },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Failed to assign supplier");
      }

      setShowSupplierSearch(false);
      onUpdate();
      toast.success("Leverancier succesvol toegewezen");
    } catch (error) {
      console.error("Error assigning supplier:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Fout bij toewijzen leverancier"
      );
    } finally {
      setSupplierLoading(false);
    }
  };

  // Handle supplier removal
  const handleSupplierRemove = async (_assignmentId: number) => {
    // Note: This would need a backend endpoint to remove supplier
    // For now, just update locally (backend update handled by parent save)
    toast.info("Leverancier verwijdering wordt opgeslagen bij opslaan...");
  };

  // Handle send RFP
  const handleSendRFP = (supplier: ContactCompany) => {
    const massMailContact = {
      id: supplier.id.toString(),
      name: supplier.name,
      resourceId: supplier.id.toString(),
      firstName: supplier.name.split(" ")[0] || supplier.name,
      lastName: supplier.name.split(" ").slice(1).join(" ") || "",
      email: supplier.email || "",
      phoneMobile: supplier.phone || "",
      units: [],
    };

    // Budget now comes from investment item assignments
    const budget = part.investmentAssignments?.reduce(
      (sum: number, assignment: any) => sum + Number(assignment.assignedAmount),
      0
    ) || 0;
    const budgetText =
      budget > 0
        ? `Budget: €${budget.toLocaleString("nl-NL", { minimumFractionDigits: 2 })}`
        : "Budget: Nog niet toegewezen";

    const projectTitle = part.project?.title || `Project ${projectId}`;
    const building = part.project?.building?.id || "Onbekend gebouw";

    const rfpContent = `
<p>Beste ${supplier.name},</p>

<p>Wij nodigen u uit om een offerte in te dienen voor het volgende projectonderdeel:</p>

<p><strong>Project:</strong> ${projectTitle}</p>
<p><strong>Gebouw:</strong> ${building}</p>
<p><strong>Projectonderdeel:</strong> ${part.title}</p>
${part.label ? `<p><strong>Categorie:</strong> ${part.label.name}</p>` : ""}
${part.description ? `<p><strong>Beschrijving:</strong><br/>${part.description}</p>` : ""}

<p><strong>Planning:</strong></p>
<ul>
  ${part.plannedStartDate ? `<li>Startdatum: ${new Date(part.plannedStartDate).toLocaleDateString("nl-NL")}</li>` : ""}
  ${part.plannedEndDate ? `<li>Einddatum: ${new Date(part.plannedEndDate).toLocaleDateString("nl-NL")}</li>` : ""}
</ul>

<p><strong>Budget:</strong> ${budgetText}</p>

<p>Graag ontvangen wij uw offerte met:</p>
<ul>
  <li>Gedetailleerde kostprijs per post</li>
  <li>Planning en doorlooptijd</li>
  <li>Garantievoorwaarden</li>
  <li>Betalingsvoorwaarden</li>
</ul>

<p>Voor vragen of meer informatie kunt u contact met ons opnemen.</p>

<p>Met vriendelijke groet</p>
    `.trim();

    const rfpState = {
      contacts: [massMailContact],
      emailSubject: `Offerte-aanvraag: ${part.title}`,
      emailContent: rfpContent,
      contextPlaceholders: {
        projectId: projectId || "",
        projectTitle: projectTitle,
        partId: part.id.toString(),
        partTitle: part.title,
        building: building,
        budget: budgetText,
      },
      selectedResources: [],
    };

    sessionStorage.setItem("massmail-context", JSON.stringify(rfpState));
    navigate("/mass-mail", { state: rfpState });
    toast.success(`Offerte-aanvraag voorbereiden voor ${supplier.name}`);
  };

  const hasSuppliers = part.supplierAssignments?.length > 0;

  return (
    <div className="relative rounded-lg border border-neutral-200 bg-white p-4">
      {/* Development Watermark */}
      <div className="absolute right-2 top-2 z-10">
        <Badge className="border-amber-300 bg-amber-100 font-mono text-amber-800">
          ** IN ONTWIKKELING **
        </Badge>
      </div>

      <div className="mb-4 flex items-center justify-between border-b border-neutral-100 pb-2">
        <h4 className="text-sm font-medium text-neutral-700">
          Leveranciers & Offerte Proces
        </h4>
        {hasSuppliers && !showSupplierSearch && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSupplierSearch(true)}
            className="border-moss-primary/30 text-moss-primary hover:bg-moss-primary/10"
          >
            <Plus className="mr-1 h-4 w-4" />
            Voeg leverancier toe
          </Button>
        )}
      </div>

      <div className="space-y-4">
        {/* Supplier Search */}
        {showSupplierSearch && (
          <div className="space-y-2" ref={searchRef}>
            <DirectSearchComponent
              resourceFilter={[ResourceType.COMPANY]}
              onSelect={(resource) => handleSupplierSelect(resource.resourceId)}
              label="Voeg leverancier toe..."
            />
            {supplierLoading && (
              <div className="flex items-center gap-2 text-sm text-neutral-500">
                <Spinner className="h-4 w-4 animate-spin" />
                <span>
                  Leverancier toevoegen en folder aanmaken onder
                  projectonderdeel...
                </span>
              </div>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSupplierSearch(false)}
              className="text-neutral-500"
            >
              Annuleren
            </Button>
          </div>
        )}

        {/* Supplier Cards */}
        {hasSuppliers ? (
          <div className="space-y-4">
            {part.supplierAssignments.map((assignment: any) => {
              const hasOffer = part.documentLines?.some(
                (da: any) =>
                  da.document.type === DocumentType.OFFER &&
                  da.document.supplier?.id === assignment.supplier.id
              );

              const hasApprovedOffer = part.documentLines?.some(
                (da: any) =>
                  da.document.type === DocumentType.OFFER &&
                  da.document.status === DocumentApprovalStatus.APPROVED &&
                  da.document.supplier?.id === assignment.supplier.id
              );

              const currentStep = hasApprovedOffer ? 3 : hasOffer ? 2 : 1;

              return (
                <div
                  key={assignment.id}
                  className="rounded-lg border border-neutral-200 bg-neutral-50/30 p-4"
                >
                  {/* Supplier Name & Remove */}
                  <div className="mb-4 flex items-center justify-between">
                    <h5 className="flex items-center gap-2 font-semibold text-neutral-800">
                      {assignment.supplier.name}
                      {hasApprovedOffer && (
                        <Badge className="bg-green-600 text-white">
                          Gekozen
                        </Badge>
                      )}
                    </h5>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSupplierRemove(assignment.id)}
                      className="text-neutral-400 hover:text-red-500"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Progress Flow */}
                  <div className="space-y-3">
                    <div className="relative flex items-center justify-between">
                      {/* Step 1: RFP Aanvragen */}
                      <div className="flex flex-1 flex-col items-center">
                        <div
                          className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                            currentStep >= 1
                              ? "border-moss-primary bg-moss-primary"
                              : "border-neutral-300 bg-white"
                          }`}
                        >
                          {currentStep >= 1 ? (
                            <CheckCircle
                              size={20}
                              weight="fill"
                              className="text-white"
                            />
                          ) : (
                            <Circle
                              size={20}
                              weight="regular"
                              className="text-neutral-300"
                            />
                          )}
                        </div>
                        <span
                          className={`mt-1 text-center text-xs ${
                            currentStep >= 1
                              ? "font-medium text-moss-primary"
                              : "text-neutral-500"
                          }`}
                        >
                          RFP aanvragen
                        </span>
                      </div>

                      {/* Connector Line 1-2 */}
                      <div
                        className="mx-2 -mt-6 h-0.5 flex-1"
                        style={{
                          background:
                            currentStep >= 2
                              ? "linear-gradient(to right, #6B9080, #6B9080)"
                              : "linear-gradient(to right, #6B9080 50%, #d4d4d8 50%)",
                        }}
                      />

                      {/* Step 2: Offerte Ontvangen */}
                      <div className="flex flex-1 flex-col items-center">
                        <div
                          className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                            currentStep >= 2
                              ? "border-moss-primary bg-moss-primary"
                              : "border-neutral-300 bg-white"
                          }`}
                        >
                          {currentStep >= 2 ? (
                            <CheckCircle
                              size={20}
                              weight="fill"
                              className="text-white"
                            />
                          ) : (
                            <Circle
                              size={20}
                              weight="regular"
                              className="text-neutral-300"
                            />
                          )}
                        </div>
                        <span
                          className={`mt-1 text-center text-xs ${
                            currentStep >= 2
                              ? "font-medium text-moss-primary"
                              : "text-neutral-500"
                          }`}
                        >
                          Offerte ontvangen
                        </span>
                      </div>

                      {/* Connector Line 2-3 */}
                      <div
                        className="mx-2 -mt-6 h-0.5 flex-1"
                        style={{
                          background:
                            currentStep >= 3
                              ? "linear-gradient(to right, #6B9080, #22c55e)"
                              : currentStep >= 2
                                ? "linear-gradient(to right, #6B9080 50%, #d4d4d8 50%)"
                                : "#d4d4d8",
                        }}
                      />

                      {/* Step 3: Offerte Beoordeeld */}
                      <div className="flex flex-1 flex-col items-center">
                        <div
                          className={`flex h-8 w-8 items-center justify-center rounded-full border-2 ${
                            currentStep >= 3
                              ? "border-green-600 bg-green-600"
                              : "border-neutral-300 bg-white"
                          }`}
                        >
                          {currentStep >= 3 ? (
                            <CheckCircle
                              size={20}
                              weight="fill"
                              className="text-white"
                            />
                          ) : (
                            <Circle
                              size={20}
                              weight="regular"
                              className="text-neutral-300"
                            />
                          )}
                        </div>
                        <span
                          className={`mt-1 text-center text-xs ${
                            currentStep >= 3
                              ? "font-medium text-green-600"
                              : "text-neutral-500"
                          }`}
                        >
                          Offerte beoordeeld
                        </span>
                      </div>
                    </div>

                    {/* Action Button - Only show for Step 1 */}
                    {currentStep === 1 && (
                      <div className="flex justify-start pt-2">
                        <Button
                          size="sm"
                          className="bg-moss-primary text-white hover:bg-moss-primary/90"
                          onClick={() => handleSendRFP(assignment.supplier)}
                        >
                          <Envelope className="mr-2 h-4 w-4" weight="fill" />
                          Verstuur RFP naar {assignment.supplier.name}
                        </Button>
                      </div>
                    )}

                    {/* Status Messages for Steps 2 & 3 */}
                    {currentStep === 2 && (
                      <div className="flex items-center gap-2 rounded-md border border-blue-200 bg-blue-50 p-2 text-sm text-neutral-600">
                        <CheckCircle
                          size={16}
                          weight="fill"
                          className="text-blue-600"
                        />
                        <span>Offerte ontvangen - wacht op beoordeling</span>
                      </div>
                    )}

                    {currentStep === 3 && (
                      <div className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 p-2 text-sm text-green-600">
                        <CheckCircle
                          size={16}
                          weight="fill"
                          className="text-green-600"
                        />
                        <span>
                          Offerte goedgekeurd - deze leverancier is gekozen
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          !showSupplierSearch && (
            <button
              onClick={() => setShowSupplierSearch(true)}
              className="group w-full rounded-lg border border-dashed border-neutral-200 bg-neutral-50 p-6 transition-colors hover:bg-neutral-100"
            >
              <div className="flex flex-col items-center space-y-2 text-center">
                <div className="rounded-full bg-moss-primary/10 p-3 transition-colors group-hover:bg-moss-primary/20">
                  <Plus size={24} className="text-moss-primary" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-medium text-neutral-700">
                    Voeg leveranciers toe
                  </p>
                  <p className="text-xs text-neutral-500">
                    Selecteer één of meerdere leveranciers om offertes aan te
                    vragen
                  </p>
                </div>
              </div>
            </button>
          )
        )}
      </div>
    </div>
  );
};

export default PartSuppliersSection;
